import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useState, useEffect } from "react";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { CompanyProvider } from "./contexts/CompanyContext";
import LoginPage from "./pages/LoginPage";
import SetupPage from "./pages/SetupPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import TeleprospectorDashboard from "./pages/TeleprospectorDashboard";
import SalesRepDashboard from "./pages/SalesRepDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import "./App.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-500">Chargement...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect to appropriate dashboard based on role
    if (user.role === "admin") return <Navigate to="/admin" replace />;
    if (user.role === "teleprospector") return <Navigate to="/teleprospector" replace />;
    if (user.role === "sales_rep") return <Navigate to="/sales-rep" replace />;
    return <Navigate to="/login" replace />;
  }

  return children;
};

const RoleBasedRedirect = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-500">Chargement...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role === "admin") return <Navigate to="/admin" replace />;
  if (user.role === "teleprospector") return <Navigate to="/teleprospector" replace />;
  if (user.role === "sales_rep") return <Navigate to="/sales-rep" replace />;

  return <Navigate to="/login" replace />;
};

// Component to check setup status and redirect accordingly
const SetupCheck = ({ children }) => {
  const [setupRequired, setSetupRequired] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const checkSetup = async () => {
      try {
        const response = await axios.get(`${API}/auth/setup-status`);
        setSetupRequired(response.data.setup_required);
      } catch (error) {
        console.error("Error checking setup status:", error);
        setSetupRequired(false);
      } finally {
        setChecking(false);
      }
    };
    checkSetup();
  }, []);

  if (checking) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-500">Vérification de la configuration...</div>
      </div>
    );
  }

  if (setupRequired) {
    return <Navigate to="/setup" replace />;
  }

  return children;
};

// Component to prevent access to setup if already done
const SetupGuard = ({ children }) => {
  const [setupRequired, setSetupRequired] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const checkSetup = async () => {
      try {
        const response = await axios.get(`${API}/auth/setup-status`);
        setSetupRequired(response.data.setup_required);
      } catch (error) {
        console.error("Error checking setup status:", error);
        setSetupRequired(false);
      } finally {
        setChecking(false);
      }
    };
    checkSetup();
  }, []);

  if (checking) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-500">Chargement...</div>
      </div>
    );
  }

  if (!setupRequired) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <CompanyProvider>
        <BrowserRouter>
          <Routes>
            <Route 
              path="/setup" 
              element={
                <SetupGuard>
                  <SetupPage />
                </SetupGuard>
              } 
            />
            <Route 
              path="/login" 
              element={
                <SetupCheck>
                  <LoginPage />
                </SetupCheck>
              } 
            />
            <Route 
              path="/forgot-password" 
              element={<ForgotPasswordPage />} 
            />
            <Route 
              path="/reset-password" 
              element={<ResetPasswordPage />} 
            />
            <Route 
              path="/" 
              element={
                <SetupCheck>
                  <RoleBasedRedirect />
                </SetupCheck>
              } 
            />
            <Route
              path="/teleprospector"
              element={
                <SetupCheck>
                  <ProtectedRoute allowedRoles={["teleprospector", "admin"]}>
                    <TeleprospectorDashboard />
                  </ProtectedRoute>
                </SetupCheck>
              }
            />
            <Route
              path="/sales-rep"
              element={
                <SetupCheck>
                  <ProtectedRoute allowedRoles={["sales_rep", "admin"]}>
                    <SalesRepDashboard />
                  </ProtectedRoute>
                </SetupCheck>
              }
            />
            <Route
              path="/admin"
              element={
                <SetupCheck>
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <AdminDashboard />
                  </ProtectedRoute>
                </SetupCheck>
              }
            />
          </Routes>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </CompanyProvider>
    </AuthProvider>
  );
}

export default App;
